/**
 * Feature videos in the product story.
 *
 * These are ambient product demonstrations, not media a visitor chooses to watch: muted,
 * looping, no chrome, playing while on screen and paused when not. That is why this does not
 * reuse the click-to-load player — a play button on a three-second suspension loop is friction
 * for no benefit.
 *
 * Nothing is fetched on page load. `src` is attached only when the video approaches the
 * viewport, so a page with three feature videos costs three poster images until the visitor
 * scrolls to them, and videos further down never download at all if they never get there.
 *
 * Reduced motion is honoured properly: the poster stays, no source is ever attached, and
 * controls are exposed so the video remains available to anyone who wants it. A looping
 * autoplaying clip is exactly what `prefers-reduced-motion` exists to suppress.
 *
 * Registered as a plain custom element rather than a Component subclass because it needs no
 * refs and no declarative events — and because the story block is not itself a component,
 * which is what silently broke the previous click-to-play implementation.
 */

const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');

class StoryVideo extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  connectedCallback() {
    this.#video = this.querySelector('video');
    if (!this.#video) return;

    if (REDUCED_MOTION.matches) {
      // Leave the poster in place and hand the visitor the controls.
      //
      // The source still has to be attached, even though nothing will autoplay. Handing over
      // controls while `src` stays on `data-src` produces a player that looks ready and does
      // nothing when pressed — a dead control reads as broken media, which is the opposite of
      // what reduced-motion is meant to deliver. `preload="none"` keeps that promise: no bytes
      // are fetched and the poster stays up until the visitor actually presses play.
      // The markup carries `autoplay` so normal visitors do not depend on a play() promise.
      // It must come off before the source is attached, or reduced motion would start the
      // clip anyway — which is the exact thing this branch exists to prevent.
      this.#video.autoplay = false;
      this.#video.removeAttribute('autoplay');

      const source = this.#video.dataset.src;
      if (source && !this.#video.src) {
        this.#video.preload = 'none';
        this.#video.src = source;
      }
      this.#video.controls = true;
      this.#video.setAttribute('data-reduced-motion', '');
      return;
    }

    // rootMargin starts the fetch just before the video is visible, so it is ready by the
    // time it scrolls in rather than starting black.
    this.#observer = new IntersectionObserver(this.#onIntersect, {
      rootMargin: '200px 0px',
      threshold: 0.15,
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    const entry = entries[0];
    const video = this.#video;
    if (!entry || !video) return;

    if (entry.isIntersecting) {
      const source = video.dataset.src;
      if (source && !video.src) {
        video.src = source;
        video.load();
      }
      // Autoplay can be refused (low power mode, data saver). Detaching the source restores
      // the poster — without this the element sits on a black first frame, because once
      // load() has run the poster no longer shows. Controls go on so the clip is still
      // watchable by anyone who wants it.
      video.play().catch((error) => {
        // Only a policy refusal means the clip will never start on its own. An AbortError is
        // just this play() losing a race with the load() above, and the next intersection
        // will start it — tearing the source down for that would break working autoplay.
        if (error?.name !== 'NotAllowedError') return;
        video.removeAttribute('src');
        video.load();
        video.controls = true;
      });
    } else {
      video.pause();
    }
  };
}

if (!customElements.get('story-video')) {
  customElements.define('story-video', StoryVideo);
}
